/**
 * Brief 26a - Policy Library (read-only)
 *
 * Lists the firm's imported policies from compliance-policy-registry-v1.0
 * for the active firm_id. Shows tier, owner_role, current version, review_by,
 * and an outstanding-CR badge per row.
 *
 * Scope: 26a Phase 1 only. No edit/write affordances.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, AlertCircle, RefreshCw } from "lucide-react";
import { authFetch } from "../../config/api";
import { ownerRoleLabel } from "./policyVocab";
import TierBadge from "./TierBadge";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function PolicyLibrary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await authFetch(`${API}/api/policy/library`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div data-testid="policy-library-loading" className="p-6">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading policy library...
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="policy-library-error" className="p-6 text-red-700">
        <AlertCircle className="inline-block mr-2" size={16} />
        Failed to load policy library: {error}
      </div>
    );
  }
  if (!data || !data.policies || data.policies.length === 0) {
    return (
      <div data-testid="policy-library-empty" className="p-6 text-gray-600">
        <BookOpen className="inline-block mr-2" size={16} />
        Policy library is empty. Run
        <code className="mx-1 px-1 bg-gray-100 rounded">python -m backend.hub.policy_import --apply</code>
        to seed registry v1.0.
      </div>
    );
  }

  const totalOpen = data.total_outstanding_crs || 0;

  return (
    <div data-testid="policy-library-page" className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Policy Library</h1>
        <div className="flex items-center gap-3">
          <span data-testid="policy-library-firm-id" className="text-sm text-gray-500">
            {data.firm_id}
          </span>
          <span
            data-testid="policy-library-total-cr-badge"
            className="inline-flex items-center px-2 py-1 rounded bg-amber-100 text-amber-900 text-sm"
          >
            {totalOpen} open
          </span>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left">
          <tr>
            <th className="px-3 py-2">Slug</th>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Tier</th>
            <th className="px-3 py-2">Owner</th>
            <th className="px-3 py-2">Version</th>
            <th className="px-3 py-2">Review By</th>
            <th className="px-3 py-2 text-right">Open CRs</th>
          </tr>
        </thead>
        <tbody>
          {data.policies.map((p) => (
            <tr
              key={p.slug}
              data-testid={`policy-library-row-${p.slug}`}
              className="border-t hover:bg-gray-50"
            >
              <td className="px-3 py-2">
                <Link
                  to={`/policy/reader/${p.slug}`}
                  className="text-blue-700 hover:underline"
                  data-testid={`policy-library-link-${p.slug}`}
                >
                  {p.slug}
                </Link>
              </td>
              <td className="px-3 py-2">{p.name}</td>
              <td className="px-3 py-2" data-testid={`policy-library-tier-cell-${p.slug}`}>
                <TierBadge tier={p.tier} testId={`policy-library-tier-${p.slug}`} />
              </td>
              <td className="px-3 py-2">{ownerRoleLabel(p.owner_role)}</td>
              <td className="px-3 py-2">{p.current_version || "-"}</td>
              <td className="px-3 py-2">{p.review_by || "-"}</td>
              <td className="px-3 py-2 text-right">
                <span data-testid={`policy-library-cr-badge-${p.slug}`}>
                  {p.outstanding_cr_count}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
